import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import {
  can,
  getAllProfiles,
  isSystemAdmin,
  softDeleteUser,
  type UserProfile,
} from '../../features/profile';
import { createUserAsAdmin } from '../../features/auth';
import { getRoles, type Role } from '../../features/roles';
import { ApiError } from '../../shared/api';
import { Card, DashboardPageHeader, DeleteConfirmModal, DashboardDataTable } from '../../shared/ui';
import CreateUserModal from '../../components/dashboard/CreateUserModal';
import EditUserModal from '../../components/dashboard/EditUserModal';

const UsersPage: React.FC = () => {
  const { user, profile } = useAuth();
  const { showToast } = useToast();
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserProfile | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const canView = !!profile && can(profile, 'users', 'view');
  const canCreate = !!profile && can(profile, 'users', 'admin');
  const canEdit = !!profile && can(profile, 'users', 'edit');
  const canRemove = !!profile && can(profile, 'users', 'delete');
  const canChangeRole = !!profile && can(profile, 'users', 'admin');

  useEffect(() => {
    if (canView) {
      void loadUsers();
    }
  }, [canView]);

  const loadUsers = async () => {
    try {
      setIsLoading(true);
      const list = await getAllProfiles();
      setUsers(list);
    } catch (err) {
      console.error(err);
      setError('Failed to load users');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRoles = async () => {
    try {
      const list = await getRoles();
      setRoles(list);
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreateUser = async (userData: {
    email: string;
    password: string;
    name: string;
    phone?: string;
    roleId: string;
  }) => {
    setError(null);
    try {
      await createUserAsAdmin(userData);
      await loadUsers();
      setShowCreateModal(false);
      showToast('User created successfully.');
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError) {
        if (err.status === 409) {
          throw new Error('A user with this email address already exists.');
        }
        if (err.status === 400) {
          throw new Error('Some details are invalid. Please check the name, email, password and role.');
        }
      }
      throw new Error('Failed to create user. Please try again.');
    }
  };

  const confirmDeleteUser = async () => {
    if (!userToDelete) return;
    const targetId = userToDelete.userId ?? userToDelete.id;
    setIsDeleting(true);
    setError(null);
    try {
      await softDeleteUser(targetId);
      setUsers((prev) => prev.filter((u) => (u.userId ?? u.id) !== targetId));
      setShowDeleteModal(false);
      setUserToDelete(null);
      showToast('User removed successfully.');
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError) {
        setError(err.message || 'Failed to remove user');
      } else {
        setError(err instanceof Error ? err.message : 'Failed to remove user');
      }
    } finally {
      setIsDeleting(false);
    }
  };

  const getRoleBadgeColor = (role: string) => (role === 'Admin' ? 'bg-danger' : 'bg-secondary');

  const formatDate = (timestamp: string | Date | null | undefined) => {
    if (!timestamp) return 'Never';
    const date = typeof timestamp === 'string' ? new Date(timestamp) : timestamp;
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!canView) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="alert alert-danger" role="alert">
            <i className="bi bi-shield-exclamation me-2"></i>
            <strong>Access Restricted:</strong> You do not have permission to view users.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <DashboardPageHeader
          title="User Management"
          description="Create users, assign roles, and manage access to the dashboard."
          icon="bi bi-people"
          actions={
            canCreate ? (
              <button
                className="btn btn-primary"
                onClick={() => {
                  void loadRoles();
                  setShowCreateModal(true);
                }}
              >
                <i className="bi bi-person-plus me-2"></i>Create User
              </button>
            ) : null
          }
        />

        {error && (
          <div className="alert alert-danger mb-3" role="alert">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {error}
          </div>
        )}

        <Card title="All Users" icon="bi bi-people-fill">
          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <p className="mt-2 text-muted">Loading users...</p>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-4">
              <i className="bi bi-people fs-1 text-muted mb-3"></i>
              <p className="text-muted">No users found</p>
            </div>
          ) : (
            <DashboardDataTable aria-label="All users">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Phone</th>
                  <th>Join Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                  {users.map((u) => {
                    const rowUserId = u.userId ?? u.id;
                    const isCurrentUser = rowUserId === user?.id;
                    return (
                    <tr key={u.id}>
                      <td>
                        <div className="d-flex align-items-center">
                          <div
                            className="bg-primary text-white rounded-circle d-flex align-items-center justify-content-center me-3"
                            style={{ width: '40px', height: '40px' }}
                          >
                            <i className="bi bi-person"></i>
                          </div>
                          <div>
                            <div className="fw-semibold">{u.name}</div>
                            {isCurrentUser && <small className="text-muted">(You)</small>}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className={`badge ${getRoleBadgeColor(u.role)}`}>{u.role}</span>
                      </td>
                      <td>
                        <span className="text-muted">{u.phone || 'Not provided'}</span>
                      </td>
                      <td>
                        <span className="text-muted">{formatDate(u.createdAt)}</span>
                      </td>
                      <td>
                        {canEdit && (
                          <button
                            className="btn btn-sm btn-outline-primary me-1"
                            title="Edit User"
                            onClick={() => {
                              void loadRoles();
                              setSelectedUser(u);
                              setShowEditModal(true);
                            }}
                          >
                            <i className="bi bi-pencil"></i>
                          </button>
                        )}
                        {canRemove &&
                          (isCurrentUser ? (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              disabled
                              title="You can't remove your own account while signed in. Use another admin account or ask a colleague to remove this user."
                              aria-disabled="true"
                            >
                              <i className="bi bi-trash" aria-hidden />
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger"
                              title="Remove user"
                              onClick={() => {
                                setUserToDelete(u);
                                setShowDeleteModal(true);
                              }}
                            >
                              <i className="bi bi-trash" aria-hidden />
                            </button>
                          ))}
                      </td>
                    </tr>
                  );
                  })}
              </tbody>
            </DashboardDataTable>
          )}
        </Card>
      </div>

      {showCreateModal && (
        <CreateUserModal
          show={showCreateModal}
          onHide={() => setShowCreateModal(false)}
          roles={roles}
          onCreateUser={handleCreateUser}
        />
      )}
      {showEditModal && selectedUser && (
        <EditUserModal
          show={showEditModal}
          onHide={() => {
            setShowEditModal(false);
            setSelectedUser(null);
          }}
          user={selectedUser}
          roles={roles}
          onUserUpdated={async () => {
            await loadUsers();
            showToast('Changes saved successfully.');
          }}
          canChangeRole={canChangeRole}
          allowPasswordReset={!!profile && isSystemAdmin(profile)}
          isEditingSelf={
            !!selectedUser &&
            !!user &&
            (selectedUser.userId ?? selectedUser.id) === user.id
          }
        />
      )}

      {showDeleteModal && userToDelete && (
        <DeleteConfirmModal
          show={showDeleteModal}
          onHide={() => {
            setShowDeleteModal(false);
            setUserToDelete(null);
          }}
          onConfirm={confirmDeleteUser}
          itemName={userToDelete.name}
          itemType="user account"
          isLoading={isDeleting}
        />
      )}
    </div>
  );
};

export default UsersPage;
